import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSystemPanel } from 'hooks/useSystemPanel';
import {
  createSystemWorker,
  deleteSystemWorker,
  updateSystemWorker,
  type Exchange,
  type LiveWorkers,
  type SystemMetrics,
  type SystemWorker,
} from 'api/systemApi';
import { useTableSort, type SortColumn, type SortDir } from './useTableSort';
import './SystemModal.css';

interface SystemModalProps {
  open: boolean;
  onClose: () => void;
}

type Tab = 'system' | 'workers' | 'config';

function fmtBytes(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function barColor(pct: number): string {
  if (pct >= 85) return 'var(--red)';
  if (pct >= 50) return 'var(--amber)';
  return 'var(--green)';
}

export function SystemModal({ open, onClose }: SystemModalProps) {
  const [tab, setTab] = useState<Tab>('system');
  const { metrics, liveWorkers, systemWorkers, error, refreshWorkers } = useSystemPanel(open);

  // Close on Escape (mirrors the Dropdown close behavior).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="sysm-overlay" onMouseDown={onClose}>
      <div className="sysm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="sysm-header">
          <div className="sysm-title">
            <span className="sysm-title-main">System Status</span>
            <span className="sysm-title-sub">live · refreshes every 2.5s</span>
          </div>
          <div className="sysm-tabs">
            <button className={`sysm-tab ${tab === 'system' ? 'active' : ''}`} onClick={() => setTab('system')}>
              System
            </button>
            <button className={`sysm-tab ${tab === 'workers' ? 'active' : ''}`} onClick={() => setTab('workers')}>
              Workers
            </button>
            <button className={`sysm-tab ${tab === 'config' ? 'active' : ''}`} onClick={() => setTab('config')}>
              System Workers
            </button>
          </div>
          <button className="sysm-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="sysm-body">
          {error && <div className="sysm-error">{error}</div>}
          {tab === 'system' && <SystemTab metrics={metrics} />}
          {tab === 'workers' && <WorkersTab liveWorkers={liveWorkers} />}
          {tab === 'config' && (
            <ConfigTab systemWorkers={systemWorkers} onChanged={refreshWorkers} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ───────────────────────────────────────────────────────────── System tab ────

function SystemTab({ metrics }: { metrics: SystemMetrics | null }) {
  if (!metrics) return <div className="sysm-loading">Loading metrics…</div>;
  const { system, analysis_pool, process, workers, services } = metrics;

  return (
    <div className="sysm-grid">
      <section className="sysm-card sysm-span2">
        <div className="sysm-card-title">
          CPU — {system.cores_busy}/{system.cpu_count_logical} cores active
          <span className="sysm-card-meta">avg {system.cpu_percent_avg}%</span>
        </div>
        <div className="sysm-cores">
          {system.per_core_percent.map((c, i) => (
            <div key={i} className="sysm-core" title={`core ${i}: ${c}%`}>
              <div className="sysm-core-track">
                <div className="sysm-core-fill" style={{ height: `${c}%`, background: barColor(c) }} />
              </div>
              <span className="sysm-core-label">{i}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="sysm-card">
        <div className="sysm-card-title">Memory</div>
        <Gauge pct={system.mem_percent} />
        <div className="sysm-kv">
          <span>Used</span>
          {/* Derive "used" as total − available so it matches the gauge (mem_percent) and the
              Available line. psutil's mem_used_bytes is a narrower active+wired figure that
              excludes reclaimable cache/buffers and would not reconcile with the percentage. */}
          <span>
            {fmtBytes(system.mem_total_bytes - system.mem_available_bytes)} / {fmtBytes(system.mem_total_bytes)}
          </span>
        </div>
        <div className="sysm-kv">
          <span>Available</span>
          <span>{fmtBytes(system.mem_available_bytes)}</span>
        </div>
      </section>

      <section className="sysm-card">
        <div className="sysm-card-title">
          Analysis pool
          <span className="sysm-card-meta">
            {analysis_pool.alive_workers}/{analysis_pool.configured_max_workers} workers
          </span>
        </div>
        <div className="sysm-kv"><span>CPU total</span><span>{analysis_pool.cpu_percent_total}%</span></div>
        <div className="sysm-kv"><span>RSS total</span><span>{fmtBytes(analysis_pool.rss_bytes_total)}</span></div>
        <div className="sysm-kv"><span>Recycle / child</span><span>{analysis_pool.max_tasks_per_child || '∞'}</span></div>
        {analysis_pool.workers.length > 0 && (
          <div className="sysm-poolworkers">
            {analysis_pool.workers.map((w) => (
              <div key={w.pid} className="sysm-poolworker">
                <span className="sysm-pid">#{w.pid}</span>
                <span>{w.cpu_percent}%</span>
                <span>{fmtBytes(w.rss_bytes)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="sysm-card">
        <div className="sysm-card-title">Main process</div>
        <div className="sysm-kv"><span>CPU</span><span>{process.cpu_percent}%</span></div>
        <div className="sysm-kv"><span>RSS</span><span>{fmtBytes(process.rss_bytes)}</span></div>
        <div className="sysm-kv"><span>Threads</span><span>{process.num_threads}</span></div>
        <div className="sysm-kv"><span>Asyncio tasks</span><span>{process.asyncio_tasks}</span></div>
        <div className="sysm-kv"><span>Open FDs</span><span>{process.num_fds ?? '—'}</span></div>
      </section>

      <section className="sysm-card">
        <div className="sysm-card-title">Workers</div>
        <div className="sysm-kv"><span>Analyzers</span><span>{workers.analyzers}</span></div>
        <div className="sysm-kv"><span>Fetchers</span><span>{workers.fetchers}</span></div>
        <div className="sysm-kv"><span>Pinned</span><span>{workers.pinned}</span></div>
        <div className="sysm-kv"><span>Subscribers</span><span>{workers.subscribers}</span></div>
        <div className="sysm-kv"><span>Analyses run</span><span>{workers.analysis_count}</span></div>
      </section>

      <section className="sysm-card sysm-span2">
        <div className="sysm-card-title">Services</div>
        <div className="sysm-pills">
          <HealthPill ok={services.database} label="Database" />
          <HealthPill ok={services.analysis_pool_initialized} label="Analysis pool" />
          <HealthPill ok={services.mail_initialized} label="Mail" />
          <HealthPill ok={services.mail_health_running} label="Mail health" />
          <HealthPill
            ok={services.event_loop_lag_ms < 100}
            label={`Loop lag ${services.event_loop_lag_ms}ms`}
          />
          <span className="sysm-pill sysm-pill-neutral">env: {services.env}</span>
        </div>
      </section>
    </div>
  );
}

function Gauge({ pct }: { pct: number }) {
  return (
    <div className="sysm-gauge">
      <div className="sysm-gauge-fill" style={{ width: `${Math.min(100, pct)}%`, background: barColor(pct) }} />
      <span className="sysm-gauge-label">{pct.toFixed(0)}%</span>
    </div>
  );
}

function HealthPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`sysm-pill ${ok ? 'sysm-pill-ok' : 'sysm-pill-bad'}`}>
      <span className="sysm-dot" />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────── sortable headers ────

/** A single sortable column header with a direction caret. */
function SortHeader({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: string;
  active: boolean;
  dir: SortDir;
  onSort: (key: string) => void;
}) {
  const activate = () => onSort(sortKey);
  const onKey = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };
  return (
    <th
      className={`sysm-th-sortable ${active ? 'sysm-th-active' : ''}`}
      tabIndex={0}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={activate}
      onKeyDown={onKey}
    >
      {label}
      <span className="sysm-th-caret">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

/** Renders a full <tr> of sortable headers for the given columns. */
function SortHeaderRow<T>({
  columns,
  sortKey,
  sortDir,
  sortBy,
  trailing,
}: {
  columns: SortColumn<T>[];
  sortKey: string | null;
  sortDir: SortDir;
  sortBy: (key: string) => void;
  trailing?: React.ReactNode;
}) {
  return (
    <tr>
      {columns.map((c) => (
        <SortHeader
          key={c.key}
          label={c.label}
          sortKey={c.key}
          active={sortKey === c.key}
          dir={sortDir}
          onSort={sortBy}
        />
      ))}
      {trailing}
    </tr>
  );
}

// ──────────────────────────────────────────────────────────── Workers tab ────

const WORKER_COLUMNS: SortColumn<LiveWorkers['analyzers'][number]>[] = [
  { key: 'kind', label: 'Kind', accessor: (w) => w.kind, type: 'string' },
  { key: 'symbol', label: 'Symbol', accessor: (w) => w.symbol, type: 'string' },
  { key: 'timeframe', label: 'TF', accessor: (w) => w.timeframe, type: 'timeframe' },
  { key: 'market', label: 'Market', accessor: (w) => w.market, type: 'string' },
  { key: 'exchange', label: 'Exchange', accessor: (w) => w.exchange, type: 'string' },
  { key: 'subscribers', label: 'Subs', accessor: (w) => w.subscribers, type: 'number' },
  { key: 'running', label: 'Running', accessor: (w) => w.running, type: 'boolean' },
  { key: 'pinned', label: 'Pinned', accessor: (w) => w.pinned, type: 'boolean' },
];

/** One independently-sortable table of live workers (analyzers or fetchers). */
function WorkerTable({ title, rows }: { title: string; rows: LiveWorkers['analyzers'] }) {
  const { sorted, sortKey, sortDir, sortBy } = useTableSort(rows, WORKER_COLUMNS);

  return (
    <div className="sysm-worker-section">
      <div className="sysm-section-title">
        {title} <span className="sysm-section-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="sysm-loading">None.</div>
      ) : (
        <table className="sysm-table">
          <thead>
            <SortHeaderRow columns={WORKER_COLUMNS} sortKey={sortKey} sortDir={sortDir} sortBy={sortBy} />
          </thead>
          <tbody>
            {sorted.map((w) => (
              <tr key={`${w.kind}:${w.key}`}>
                <td>{w.kind}</td>
                <td>{w.symbol}</td>
                <td>{w.timeframe}</td>
                <td>{w.market}</td>
                <td>{w.exchange}</td>
                <td>{w.subscribers}</td>
                <td>{w.running ? '✓' : '✕'}</td>
                <td>{w.pinned ? '📌' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WorkersTab({ liveWorkers }: { liveWorkers: LiveWorkers | null }) {
  if (!liveWorkers) return <div className="sysm-loading">Loading workers…</div>;

  return (
    <div className="sysm-worker-groups">
      <WorkerTable title="Analyzers" rows={liveWorkers.analyzers} />
      <WorkerTable title="Fetchers" rows={liveWorkers.fetchers} />
    </div>
  );
}

// ─────────────────────────────────────────────────────── System workers tab ──

// Exchange already identifies the group, so it's dropped from the per-group columns.
const SYSTEM_WORKER_COLUMNS: SortColumn<SystemWorker>[] = [
  { key: 'symbol', label: 'Symbol', accessor: (w) => w.symbol, type: 'string' },
  { key: 'timeframe', label: 'TF', accessor: (w) => w.timeframe, type: 'timeframe' },
  { key: 'market', label: 'Market', accessor: (w) => (w.use_futures ? 'futures' : 'spot'), type: 'string' },
  { key: 'live', label: 'Live', accessor: (w) => w.live, type: 'boolean' },
  { key: 'enabled', label: 'Enabled', accessor: (w) => w.enabled, type: 'boolean' },
];

const SYMBOL_OPTIONS = ['BTCUSDT', 'ETHUSDT'] as const;
const TIMEFRAME_OPTIONS = ['5m', '15m', '30m', '1h', '4h', '12h', '1d', '3d'] as const;
const EXCHANGE_OPTIONS: Exchange[] = ['binance', 'mexc'];

/** One exchange's worker rows, sorted independently, with toggle/delete controls. */
function SystemWorkerTable({
  exchange,
  rows,
  onToggle,
  onRemove,
}: {
  exchange: string;
  rows: SystemWorker[];
  onToggle: (w: SystemWorker) => void;
  onRemove: (w: SystemWorker) => void;
}) {
  const { sorted, sortKey, sortDir, sortBy } = useTableSort(rows, SYSTEM_WORKER_COLUMNS);

  return (
    <div className="sysm-worker-section">
      <div className="sysm-section-title">
        {exchange} <span className="sysm-section-count">{rows.length}</span>
      </div>
      <table className="sysm-table">
        <thead>
          <SortHeaderRow
            columns={SYSTEM_WORKER_COLUMNS}
            sortKey={sortKey}
            sortDir={sortDir}
            sortBy={sortBy}
            trailing={<th />}
          />
        </thead>
        <tbody>
          {sorted.map((w) => (
            <tr key={w.id} className={w.enabled ? '' : 'sysm-row-off'}>
              <td>{w.symbol}</td>
              <td>{w.timeframe}</td>
              <td>{w.use_futures ? 'futures' : 'spot'}</td>
              <td>{w.live ? '🟢' : '⚪'}</td>
              <td>
                <button
                  className={`sysm-toggle ${w.enabled ? 'on' : 'off'}`}
                  onClick={() => onToggle(w)}
                  title={w.enabled ? 'Disable' : 'Enable'}
                >
                  <span className="sysm-toggle-knob" />
                </button>
              </td>
              <td>
                <button className="sysm-del" onClick={() => onRemove(w)} title="Delete">
                  🗑
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfigTab({
  systemWorkers,
  onChanged,
}: {
  systemWorkers: SystemWorker[];
  onChanged: () => void;
}) {
  const [symbol, setSymbol] = useState<string>(SYMBOL_OPTIONS[0]);
  const [timeframe, setTimeframe] = useState<string>('15m');
  const [exchange, setExchange] = useState<Exchange>('binance');
  const [useFutures, setUseFutures] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createSystemWorker({
        symbol: symbol.trim().toUpperCase(),
        timeframe: timeframe.trim(),
        exchange,
        use_futures: exchange === 'mexc' ? false : useFutures,
      });
      onChanged();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (w: SystemWorker) => {
    try {
      await updateSystemWorker(w.id, { enabled: !w.enabled });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (w: SystemWorker) => {
    try {
      await deleteSystemWorker(w.id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  // Group rows by exchange, listing the known exchanges first, then any others present.
  const byExchange = new Map<string, SystemWorker[]>();
  for (const w of systemWorkers) {
    const list = byExchange.get(w.exchange) ?? [];
    list.push(w);
    byExchange.set(w.exchange, list);
  }
  const exchanges = [
    ...EXCHANGE_OPTIONS.filter((ex) => byExchange.has(ex)),
    ...[...byExchange.keys()].filter((ex) => !EXCHANGE_OPTIONS.includes(ex as Exchange)),
  ];

  return (
    <div className="sysm-config">
      <form className="sysm-addform" onSubmit={add}>
        <select
          className="sysm-input"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          aria-label="Symbol"
        >
          {SYMBOL_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="sysm-input sysm-input-sm"
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          aria-label="Timeframe"
        >
          {TIMEFRAME_OPTIONS.map((tf) => (
            <option key={tf} value={tf}>
              {tf}
            </option>
          ))}
        </select>
        <select
          className="sysm-input sysm-input-sm"
          value={exchange}
          onChange={(e) => setExchange(e.target.value as Exchange)}
          aria-label="Exchange"
        >
          {EXCHANGE_OPTIONS.map((ex) => (
            <option key={ex} value={ex}>
              {ex}
            </option>
          ))}
        </select>
        <label className={`sysm-check ${exchange === 'mexc' ? 'disabled' : ''}`}>
          <input
            type="checkbox"
            checked={useFutures}
            disabled={exchange === 'mexc'}
            onChange={(e) => setUseFutures(e.target.checked)}
          />
          futures
        </label>
        <button className="sysm-addbtn" type="submit" disabled={busy}>
          {busy ? 'Adding…' : '+ Add'}
        </button>
      </form>
      {err && <div className="sysm-error">{err}</div>}

      {exchanges.length === 0 ? (
        <div className="sysm-loading">No system workers configured.</div>
      ) : (
        <div className="sysm-worker-groups">
          {exchanges.map((ex) => (
            <SystemWorkerTable
              key={ex}
              exchange={ex}
              rows={byExchange.get(ex) ?? []}
              onToggle={toggle}
              onRemove={remove}
            />
          ))}
        </div>
      )}
    </div>
  );
}
